/**
 * Herb & Habit continuous announcement ticker
 *
 * Builds lightweight ticker items from the Shopify
 * announcement blocks, then animates the track using
 * the Web Animations API instead of CSS keyframes.
 */

class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    this.source = null;
    this.viewport = null;
    this.track = null;

    this.animation = null;

    this.resizeObserver = null;
    this.mutationObserver = null;

    this.resizeTimer = null;
    this.mutationTimer = null;

    this.handleMouseEnter =
      this.handleMouseEnter.bind(this);

    this.handleMouseLeave =
      this.handleMouseLeave.bind(this);
  }


  connectedCallback() {
    this.source =
      this.querySelector(
        '[data-announcement-source]'
      );

    this.viewport =
      this.querySelector(
        '[data-announcement-viewport]'
      );

    this.track =
      this.querySelector(
        '[data-announcement-track]'
      );


    if (
      !this.source ||
      !this.viewport ||
      !this.track
    ) {
      return;
    }


    requestAnimationFrame(() => {
      this.build();
    });


    /* ------------------------------
       Pause on hover
       ------------------------------ */

    this.addEventListener(
      'mouseenter',
      this.handleMouseEnter
    );

    this.addEventListener(
      'mouseleave',
      this.handleMouseLeave
    );


    /* ------------------------------
       Rebuild on resize
       ------------------------------ */

    this.resizeObserver =
      new ResizeObserver(() => {

        clearTimeout(
          this.resizeTimer
        );

        this.resizeTimer =
          setTimeout(() => {
            this.build();
          }, 150);

      });


    this.resizeObserver.observe(
      this.viewport
    );


    /* ------------------------------
       Rebuild when announcement
       blocks are edited in Shopify
       ------------------------------ */

    this.mutationObserver =
      new MutationObserver(() => {

        clearTimeout(
          this.mutationTimer
        );

        this.mutationTimer =
          setTimeout(() => {
            this.build();
          }, 100);

      });


    this.mutationObserver.observe(
      this.source,
      {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true
      }
    );
  }


  disconnectedCallback() {

    this.stopAnimation();


    this.removeEventListener(
      'mouseenter',
      this.handleMouseEnter
    );

    this.removeEventListener(
      'mouseleave',
      this.handleMouseLeave
    );


    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }


    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }


    clearTimeout(
      this.resizeTimer
    );

    clearTimeout(
      this.mutationTimer
    );
  }


  /* ==========================================
     PAUSE / RESUME
     ========================================== */

  handleMouseEnter() {

    const section =
      this.closest(
        '.announcement-bar'
      );


    if (
      section?.classList.contains(
        'announcement-bar--pause-on-hover'
      )
    ) {
      this.animation?.pause();
    }

  }


  handleMouseLeave() {

    if (
      this.animation &&
      this.animation.playState === 'paused'
    ) {
      this.animation.play();
    }

  }


  /* ==========================================
     GET ORIGINAL ANNOUNCEMENTS
     ========================================== */

  getSlides() {

    return Array.from(
      this.source.querySelectorAll(
        '.announcement-bar__slide'
      )
    );

  }


  /* ==========================================
     CREATE CLEAN TICKER ITEM
     ========================================== */

  createItem(
    slide,
    decorative = false
  ) {

    const textElement =
      slide.querySelector(
        '.announcement-bar__text'
      );


    if (!textElement) {
      return null;
    }


    const linkElement =
      slide.querySelector(
        '.announcement-bar__link'
      );


    const href =
      linkElement?.getAttribute(
        'href'
      );


    const item =
      document.createElement(
        href ? 'a' : 'span'
      );


    item.className =
      'announcement-bar__item';


    item.innerHTML =
      textElement.innerHTML;


    if (href) {
      item.href = href;
    }


    /* Preserve Theme Editor typography */

    const styles =
      window.getComputedStyle(
        slide
      );


    item.style.fontFamily =
      styles.fontFamily;

    item.style.fontSize =
      styles.fontSize;

    item.style.fontWeight =
      styles.fontWeight;

    item.style.letterSpacing =
      styles.letterSpacing;

    item.style.textTransform =
      styles.textTransform;

    item.style.color =
      styles.color;


    if (decorative) {

      item.setAttribute(
        'aria-hidden',
        'true'
      );

      item.setAttribute(
        'tabindex',
        '-1'
      );

    }


    return item;
  }


  /* ==========================================
     ADD ONE FULL MESSAGE SEQUENCE
     ========================================== */

  addSequence(
    group,
    slides,
    decorative = false
  ) {

    slides.forEach((slide) => {

      const item =
        this.createItem(
          slide,
          decorative
        );


      if (item) {
        group.appendChild(item);
      }

    });

  }


  /* ==========================================
     READ SCROLL SPEED FROM SHOPIFY SETTING
     ========================================== */

  getDuration() {

    const section =
      this.closest(
        '.announcement-bar'
      );


    if (!section) {
      return 24000;
    }


    const raw =
      getComputedStyle(section)
        .getPropertyValue(
          '--ticker-duration'
        )
        .trim();


    const seconds =
      parseFloat(raw);


    if (
      !Number.isFinite(seconds) ||
      seconds <= 0
    ) {
      return 24000;
    }


    return seconds * 1000;
  }


  /* ==========================================
     STOP EXISTING ANIMATION
     ========================================== */

  stopAnimation() {

    if (this.animation) {

      this.animation.cancel();

      this.animation = null;

    }


    if (this.track) {

      this.track.style.transform =
        'translate3d(0, 0, 0)';

    }

  }


  /* ==========================================
     START CONTINUOUS MOVEMENT
     ========================================== */

  startAnimation(groupWidth) {

    this.stopAnimation();


    if (!groupWidth) {
      return;
    }


    const duration =
      this.getDuration();


    /*
      Move exactly one complete ticker group.

      The second group is identical, so when
      the animation loops it appears seamless.
    */

    this.animation =
      this.track.animate(
        [
          {
            transform:
              'translate3d(0, 0, 0)'
          },
          {
            transform:
              `translate3d(-${groupWidth}px, 0, 0)`
          }
        ],
        {
          duration: duration,
          iterations: Infinity,
          easing: 'linear'
        }
      );

  }


  /* ==========================================
     BUILD TICKER
     ========================================== */

  build() {

    const slides =
      this.getSlides();


    if (!slides.length) {
      return;
    }


    this.stopAnimation();


    this.track.replaceChildren();


    /* ------------------------------
       Build first group
       ------------------------------ */

    const group =
      document.createElement(
        'div'
      );


    group.className =
      'announcement-bar__group';


    this.addSequence(
      group,
      slides,
      false
    );


    this.track.appendChild(
      group
    );


    /* ------------------------------
       Make sure first group fills
       more than the screen width
       ------------------------------ */

    const requiredWidth =
      this.viewport.clientWidth * 1.15;


    let repetitions = 0;


    while (
      group.scrollWidth <
        requiredWidth &&
      repetitions < 20
    ) {

      this.addSequence(
        group,
        slides,
        true
      );

      repetitions++;

    }


    /* ------------------------------
       Measure exact width
       ------------------------------ */

    const groupWidth =
      group.getBoundingClientRect().width;


    /* ------------------------------
       Duplicate group
       ------------------------------ */

    const duplicate =
      group.cloneNode(true);


    duplicate.setAttribute(
      'aria-hidden',
      'true'
    );


    duplicate.setAttribute(
      'inert',
      ''
    );


    duplicate
      .querySelectorAll('a')
      .forEach((link) => {

        link.setAttribute(
          'tabindex',
          '-1'
        );

      });


    this.track.appendChild(
      duplicate
    );


    /* ------------------------------
       Start movement
       ------------------------------ */

    requestAnimationFrame(() => {

      this.startAnimation(
        groupWidth
      );

    });

  }
}


if (
  !customElements.get(
    'announcement-bar-component'
  )
) {

  customElements.define(
    'announcement-bar-component',
    AnnouncementBar
  );

}